import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import { logger } from '@/utils/logger';
import type { ProfileEvent, EventPreviewModalProps, OrganizationInfo, EventStats, VolunteerInfo } from './types';
import { hasAcceptedOrgMembership } from '@/hooks/useAcceptedMembershipOrgIds';
import { insertNotification } from '@/utils/notificationInsert';
import { fetchProfileDisplayName } from '@/utils/notificationHelpers';
import { formatDuration, minutesToHours, validateDuration } from '@/utils/volunteerHours';
import { buildEventInviteLink } from '@/utils/buildEventInviteLink';
import { fetchEventPublicStats } from '@/utils/eventPublicStats';

export function useEventPreviewData(
  visible: boolean,
  event: ProfileEvent | null,
  props: Pick<
    EventPreviewModalProps,
    'isOrganization' | 'fromInviteLink' | 'onJoinEvent' | 'onClose'
  >
) {
  const { isOrganization = false, fromInviteLink = false, onJoinEvent, onClose } = props;
  const [organization, setOrganization] = useState<OrganizationInfo | null>(null);
  const [stats, setStats] = useState<EventStats>({ totalVolunteers: 0, totalHours: 0, filledSlots: 0 });
  const [isAlreadyVolunteer, setIsAlreadyVolunteer] = useState(false);
  const [isAcceptedVolunteer, setIsAcceptedVolunteer] = useState(false);
  const [volunteerInfo, setVolunteerInfo] = useState<VolunteerInfo | null>(null);
  const [currentEvent, setCurrentEvent] = useState(event || null);
  const [incompleteTasksCount, setIncompleteTasksCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [creatingHours, setCreatingHours] = useState(false);
  const currentEventIdRef = useRef<string | null>(null);
  const { user } = useAuth();
  const { showToast, hideToast, toast } = useToast();

  useEffect(() => {
    if (event?.id) {
      currentEventIdRef.current = event.id;
      setCurrentEvent(event);
      setIsAlreadyVolunteer(false);
      setIsAcceptedVolunteer(false);
      setVolunteerInfo(null);
      setOrganization(null);
      setStats({ totalVolunteers: 0, totalHours: 0, filledSlots: 0 });
    } else {
      currentEventIdRef.current = null;
      setCurrentEvent(null);
      setStats({ totalVolunteers: 0, totalHours: 0, filledSlots: 0 });
    }
    // Reset only when a different event is opened; parents pass a fresh object for the same
    // event on re-render, and resetting on that would wipe the loaded details.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id]);

  useEffect(() => {
    if (event?.id) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [event?.id]);

  useEffect(() => {
    if (!visible || !currentEvent) return;

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchEventDetails(),
          checkIfAlreadyVolunteer(),
          !isOrganization && user?.id ? fetchIncompleteTasksCount() : Promise.resolve(),
        ]);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // The fetchers are redefined every render and fetchEventDetails updates currentEvent,
    // so depending on them (or on the whole currentEvent) would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, currentEvent?.id, isOrganization, user?.id]);

  useEffect(() => {
    if (visible && isOrganization && user?.id && !organization) {
      const fetchCurrentOrg = async () => {
        const { data: currentOrgData, error: currentOrgError } = await supabase
          .from('profiles')
          .select('id, first_name, profile_image, email, phone, description')
          .eq('id', user.id)
          .eq('role', 'organization')
          .single();
        if (!currentOrgError && currentOrgData) {
          setOrganization({
            id: currentOrgData.id,
            name: currentOrgData.first_name,
            profile_image: currentOrgData.profile_image,
            contact_email: currentOrgData.email,
            contact_phone: currentOrgData.phone,
            description: currentOrgData.description,
          });
        }
      };
      fetchCurrentOrg();
    }
  }, [visible, isOrganization, user?.id, organization]);

  const fetchEventDetails = async () => {
    if (!currentEvent?.id) return;
    const eventId = currentEvent.id;
    try {
      let eventData: Record<string, unknown> | null = null;
      if (fromInviteLink) {
        const { data, error: inviteError } = await supabase.rpc('get_event_for_invite', {
          p_event_id: eventId,
        });
        if (inviteError) {
          logger.error('Error fetching invite event details:', inviteError);
          return;
        }
        const row = Array.isArray(data) ? data[0] : data;
        eventData = row && typeof row === 'object' ? (row as Record<string, unknown>) : null;
      } else {
        const { data, error: eventError } = await supabase
          .from('events')
          .select('*')
          .eq('id', eventId)
          .single();
        if (eventError) {
          logger.error('Error fetching event details:', eventError);
          return;
        }
        eventData = data as Record<string, unknown> | null;
      }

      if (eventData) {
        setCurrentEvent((prev) => (prev?.id !== eventId ? prev : { ...prev, ...eventData }));
      }
      const createdBy = eventData?.created_by as string | undefined;
      if (createdBy) {
        const { data: orgData, error: orgError } = await supabase
          .from('profiles')
          .select('id, first_name, profile_image, email, phone, description')
          .eq('id', createdBy)
          .eq('role', 'organization')
          .single();
        if (!orgError && orgData) {
          setOrganization((prev) =>
            currentEvent?.id !== eventId
              ? prev
              : {
                  id: orgData.id,
                  name: orgData.first_name,
                  profile_image: orgData.profile_image,
                  contact_email: orgData.email,
                  contact_phone: orgData.phone,
                  description: orgData.description,
                }
          );
        }
      }
      // Aggregated server-side rather than counted from `volunteers` rows: RLS only
      // exposes those rows to the organizing org, the volunteer themselves and members
      // of the event, and returns an empty set — not an error — to everyone else, so
      // counting them here rendered 0 on every event the viewer had not joined.
      const statsByEventId = await fetchEventPublicStats([eventId]);
      const eventStats = statsByEventId[eventId];
      if (eventStats && currentEventIdRef.current === eventId) {
        setStats({
          totalVolunteers: eventStats.volunteerCount,
          totalHours: eventStats.totalHours,
          filledSlots: eventStats.volunteerCount,
        });
      }
    } catch (error) {
      logger.error('Error fetching event details:', error);
    }
  };

  const createVolunteerHoursRequest = async (params: {
    start_time: string;
    end_time: string;
    minutes: number;
    description: string | null;
  }): Promise<boolean> => {
    if (!user?.id || !currentEvent?.id || isOrganization) {
      showToast('You must be a volunteer in this event to add hours.', 'error');
      return false;
    }
    const durationError = validateDuration(params.minutes);
    if (durationError) {
      showToast(durationError, 'error');
      return false;
    }
    const hours = minutesToHours(params.minutes);
    try {
      setCreatingHours(true);
      const trimmedDescription = params.description?.trim() ?? '';
      const insertPayload: {
        volunteer_id: string;
        event_id: string;
        start_time: string;
        end_time: string;
        hours: number;
        description?: string | null;
      } = {
        volunteer_id: user.id,
        event_id: currentEvent.id,
        start_time: params.start_time,
        end_time: params.end_time,
        hours,
      };
      if (trimmedDescription.length > 0) {
        insertPayload.description = trimmedDescription;
      }

      const { data: activity, error } = await supabase
        .from('volunteer_activities')
        .insert(insertPayload)
        .select('id')
        .single();
      if (error) {
        showToast(error.message || 'Failed to send hours request.', 'error');
        return false;
      }

      const { data: eventRow } = await supabase
        .from('events')
        .select('id, name, created_by')
        .eq('id', currentEvent.id)
        .maybeSingle();

      const orgId = eventRow?.created_by;
      if (orgId && activity?.id) {
        const volunteerName = await fetchProfileDisplayName(user.id);
        const eventLabel = eventRow?.name?.trim() || currentEvent.name?.trim() || 'your event';
        insertNotification({
          user_id: orgId,
          title: 'Hours request',
          message: `${volunteerName} requested ${formatDuration(hours)} for ${eventLabel}.`,
          type: 'hours_request_pending',
          data: {
            activity_id: activity.id,
            event_id: currentEvent.id,
            volunteer_id: user.id,
            hours,
            event_name: eventLabel,
            volunteer_name: volunteerName,
          },
          read: false,
        });
      }

      showToast('Hours request sent for approval.', 'success');
      return true;
    } catch (err) {
      logger.error('Error creating volunteer hours request:', err);
      showToast(
        (err instanceof Error && err.message) || 'Failed to send hours request.',
        'error',
      );
      return false;
    } finally {
      setCreatingHours(false);
    }
  };

  const checkIfAlreadyVolunteer = async () => {
    if (!currentEvent?.id || !user?.id || isOrganization) {
      setIsAlreadyVolunteer(false);
      setIsAcceptedVolunteer(false);
      setVolunteerInfo(null);
      return;
    }
    try {
      const { data: applicationData } = await supabase
        .from('volunteer_applications')
        .select('id, status')
        .eq('event_id', currentEvent.id)
        .eq('volunteer_id', user.id)
        .maybeSingle();
      const { data: volunteerData, error: volunteerError } = await supabase
        .from('volunteers')
        .select('id, department, hours, volunteer_id')
        .eq('event_id', currentEvent.id)
        .eq('volunteer_id', user.id)
        .eq('is_active', true)
        .maybeSingle();
      if (applicationData || (volunteerData && !volunteerError)) {
        setIsAlreadyVolunteer(true);
        if (volunteerData && !volunteerError) {
          setIsAcceptedVolunteer(true);
          const { data: profileData } = await supabase
            .from('profiles')
            .select('profile_image, first_name, last_name')
            .eq('id', volunteerData.volunteer_id)
            .single();
          setVolunteerInfo({
            id: volunteerData.id,
            department: volunteerData.department,
            hours: volunteerData.hours,
            profile_image: profileData?.profile_image,
            first_name: profileData?.first_name,
            last_name: profileData?.last_name,
          });
        } else {
          // A pending application, not a place on the event.
          setIsAcceptedVolunteer(false);
          setVolunteerInfo(null);
        }
        return;
      }
      setIsAlreadyVolunteer(false);
      setIsAcceptedVolunteer(false);
      setVolunteerInfo(null);
    } catch {
      setIsAlreadyVolunteer(false);
      setIsAcceptedVolunteer(false);
      setVolunteerInfo(null);
    }
  };

  const fetchIncompleteTasksCount = async () => {
    if (!currentEvent?.id || !user?.id || isOrganization) {
      setIncompleteTasksCount(0);
      return;
    }
    try {
      const { data: volunteerRecord } = await supabase
        .from('volunteers')
        .select('department')
        .eq('event_id', currentEvent.id)
        .eq('volunteer_id', user.id)
        .eq('is_active', true)
        .maybeSingle();
      const volunteerDepartments = volunteerRecord?.department
        ? volunteerRecord.department
            .split(',')
            .map((d: string) => d.trim())
            .filter(Boolean)
        : [];
      const { data: tasks, error } = await supabase
        .from('tasks')
        .select('assigned_volunteers, assigned_departments')
        .eq('event_id', currentEvent.id)
        .in('status', ['pending', 'in_progress']);
      if (error) {
        logger.error('Error fetching incomplete tasks count:', error);
        setIncompleteTasksCount(0);
        return;
      }
      const count = (tasks || []).filter((task) => {
        const assignedVolunteers = Array.isArray(task.assigned_volunteers) ? task.assigned_volunteers : [];
        const assignedDepartments = Array.isArray(task.assigned_departments) ? task.assigned_departments : [];
        if (assignedVolunteers.includes(user.id)) return true;
        if (volunteerDepartments.length > 0 && assignedDepartments.length > 0) {
          const normalized = assignedDepartments.map((d: string) => d.trim().toLowerCase());
          return volunteerDepartments.some((d: string) => normalized.includes(d.toLowerCase()));
        }
        return false;
      }).length;
      setIncompleteTasksCount(count);
    } catch (error) {
      logger.error('Error fetching incomplete tasks count:', error);
      setIncompleteTasksCount(0);
    }
  };

  const handleShareEvent = async () => {
    if (!currentEvent?.id) {
      showToast('Event information not available', 'error');
      return;
    }
    try {
      const eventLink = buildEventInviteLink(currentEvent.id);
      await navigator.clipboard.writeText(eventLink);
      showToast('Event link copied!', 'success');
    } catch (error) {
      logger.error('Failed to copy event link:', error);
      showToast('Failed to copy event link. Please try again.', 'error');
    }
  };

  const handleJoinEvent = async () => {
    if (!onJoinEvent || !currentEvent) return;
    if (!currentEvent.is_public && !fromInviteLink) {
      const orgId = currentEvent.created_by;
      if (!user?.id || !orgId) {
        showToast('This is a private event. You need an invite link to join.', 'error');
        return;
      }
      const allowed = await hasAcceptedOrgMembership(user.id, orgId);
      if (!allowed) {
        showToast('This is a private event. You need an invite link to join.', 'error');
        return;
      }
    }
    if (
      currentEvent?.slots != null &&
      currentEvent?.volunteerCount != null &&
      Number(currentEvent.volunteerCount) >= Number(currentEvent.slots)
    ) {
      showToast('This event has reached its maximum capacity and cannot accept more volunteers.', 'error');
      return;
    }
    try {
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('slots')
        .eq('id', currentEvent.id)
        .single();
      if (eventError) {
        showToast('Unable to verify event availability. Please try again.', 'error');
        return;
      }
      // Same reason as the stats above: counting `volunteers` rows directly returned 0
      // for anyone not already on the event, so this capacity guard never fired for the
      // very users it exists to stop.
      const joinStats = await fetchEventPublicStats([currentEvent.id]);
      const eventJoinStats = joinStats[currentEvent.id];
      if (!eventJoinStats) {
        showToast('Unable to verify event availability. Please try again.', 'error');
        return;
      }
      const currentVolunteerCount = eventJoinStats.volunteerCount;
      if (eventData.slots && currentVolunteerCount >= Number(eventData.slots)) {
        showToast('This event has just reached its maximum capacity. Please try another event.', 'error');
        return;
      }
      onJoinEvent(currentEvent);
    } catch (error) {
      logger.error('Error in handleJoinEvent:', error);
      showToast('Unable to verify event availability. Please try again.', 'error');
    }
  };

  return {
    organization,
    stats,
    isAlreadyVolunteer,
    isAcceptedVolunteer,
    volunteerInfo,
    currentEvent,
    incompleteTasksCount,
    refreshVolunteerState: checkIfAlreadyVolunteer,
    toast,
    hideToast,
    showToast,
    user,
    handleShareEvent,
    handleJoinEvent,
    onClose,
    loading,
    creatingHours,
    createVolunteerHoursRequest,
  };
}
